import { type ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MAX_ITEM_TYPE_ROWS } from "../../../constants";
import Button from "../../Button";
import { SessionReportCard } from "../SessionReportCard";
import type { SessionReportItemTypeDetailSelection } from "../SessionReportItemTypeDetailDialog";
import { SessionReportQuestionTypeRowList } from "./SessionReportQuestionTypeRowList";
import {
	type SessionReportQuestionTypeCardData,
	getDistributionForItems,
	groupQuestionItemsByQuestionType,
	isQuestionItem,
	sortQuestionTypeRows,
} from "./helpers";

export type { SessionReportQuestionTypeCardData as Data } from "./helpers";

type Props = SessionReportQuestionTypeCardData & {
	onViewAll?: () => void;
	isSummativeTest?: boolean;
	onRowPress: (selection: SessionReportItemTypeDetailSelection) => void;
	renderFooter?: () => ReactNode;
};

export const SessionReportQuestionTypeCard = ({
	title,
	subtitle,
	icon,
	filter,
	tagItems,
	sessionItems,
	proficiencyTags,
	onViewAll,
	isSummativeTest = false,
	onRowPress,
	renderFooter,
}: Props) => {
	const { t } = useTranslation();

	const questionItemsByQuestionType = useMemo(() => {
		const questionItems = tagItems.filter((item) => isQuestionItem(item));
		return groupQuestionItemsByQuestionType(questionItems, sessionItems);
	}, [tagItems, sessionItems]);

	const rows = useMemo(
		() =>
			sortQuestionTypeRows(
				Object.entries(questionItemsByQuestionType).map(
					([questionType, items]) => ({
						questionType,
						numItems: items.length,
						distribution: getDistributionForItems(items),
					}),
				),
				sessionItems,
			),
		[questionItemsByQuestionType, sessionItems],
	);

	if (rows.length === 0) {
		return null;
	}

	const visibleRows = onViewAll ? rows.slice(0, MAX_ITEM_TYPE_ROWS) : rows;
	const showViewAll = Boolean(onViewAll) && rows.length !== visibleRows.length;

	return (
		<SessionReportCard.Frame>
			<SessionReportCard.Header
				icon={icon}
				title={title}
				subtitle={subtitle}
				trailing={
					showViewAll ? (
						<Button mode="text" onPress={onViewAll}>
							{t("common.viewAll")}
						</Button>
					) : undefined
				}
			/>
			<SessionReportCard.Body>
				<SessionReportQuestionTypeRowList
					rows={visibleRows}
					questionItemsByQuestionType={questionItemsByQuestionType}
					sessionItems={sessionItems}
					isSummativeTest={isSummativeTest}
					proficiencyTags={proficiencyTags}
					skill={filter}
					onRowPress={onRowPress}
				/>
			</SessionReportCard.Body>
			{renderFooter ? (
				<SessionReportCard.Footer
					paddingHorizontal={16}
					paddingTop={8}
					paddingBottom={16}
					flexDirection="column"
					alignItems="stretch"
					render={renderFooter}
				/>
			) : null}
		</SessionReportCard.Frame>
	);
};
